import { Typography } from '@material-ui/core';
import * as SDK from '@replai-platform/sdk';
import styled from 'styled-components';
import type { Row } from 'react-table';
import { Skeleton } from '@replai-platform/ui-components';
import { logEvent } from '../../../analytics';
import ClusterName from '../../ClusterName';
import { CellData, GeneratedColumn, RequiredGeneratorParams } from '../common';

const CreativeInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export type NameAccessorType = {
  title: string;
  originalName?: string;
  subTitle: string;
  objectId?: string;
  conceptId?: string;
  projectId?: string;
  tagId?: string | null;
  loading?: boolean;
  rowHref?: string;
  allowEdit?: boolean;
};

const NameCellDecorator: React.FC<{
  cellData: NameAccessorType;
  row: Row<any>;
}> = ({ cellData, row }) => {
  if (!cellData) {
    return null;
  }

  if (cellData.loading) {
    return <Skeleton height={20} width={100} />;
  }

  return !cellData.tagId ? (
    <ClusterName
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      objectId={cellData.objectId!}
      conceptId={cellData.conceptId}
      name={cellData.title || SDK.messages.NOT_AVAILABLE}
      originalName={cellData.originalName}
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      projectId={cellData.projectId!}
      additionalInfo={cellData.subTitle || SDK.messages.NOT_AVAILABLE}
      videoHref={cellData.rowHref}
      eventOrigin="Videos"
      allowEdit={cellData.allowEdit}
      linkOnClick={() => {
        logEvent({
          component: 'Videos',
          action: row.depth === 0 ? 'Click on Row' : 'Click on Subrow',
          category: 'user_actions',
          parameters: {
            assetId: row.depth === 0 ? cellData.conceptId : cellData.objectId,
          },
        });
      }}
    />
  ) : (
    <CreativeInfo>
      <Typography variant="body2">{cellData.title}</Typography>
      <Typography variant="body1">{cellData.subTitle}</Typography>
    </CreativeInfo>
  );
};

function getClusterNameColumn<T extends object = NameAccessorType>({
  columnId,
  accessor,
}: RequiredGeneratorParams<T, NameAccessorType>): GeneratedColumn<T> {
  return {
    id: columnId,
    accessor,
    Cell: (data: CellData<T, NameAccessorType>) => (
      <NameCellDecorator cellData={data?.cell?.value} row={data?.cell?.row} />
    ),
  };
}

export default getClusterNameColumn;
